import { useRef } from "react";
import { motion, useInView } from "motion/react";
import {
  Database,
  Brain,
  ShoppingCart,
  Banknote,
  Ship,
  ShieldCheck,
  FileCheck,
  Zap,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

/* Solid gradient flow connector (horizontal) with animated traveling dot */
const FlowLine = ({
  delay = 0,
  inView,
}: {
  delay?: number;
  inView: boolean;
}) => (
  <div className="flex items-center justify-center">
    <svg
      width="48"
      height="24"
      viewBox="0 0 48 24"
      className="overflow-visible"
    >
      <defs>
        <linearGradient id={`hg-${delay}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop
            offset="0%"
            stopColor="hsl(var(--primary))"
            stopOpacity="0.12"
          />
          <stop
            offset="50%"
            stopColor="hsl(var(--primary))"
            stopOpacity="0.4"
          />
          <stop
            offset="100%"
            stopColor="hsl(var(--primary))"
            stopOpacity="0.12"
          />
        </linearGradient>
      </defs>
      {/* Base line */}
      <motion.line
        x1="0"
        y1="12"
        x2="42"
        y2="12"
        stroke={`url(#hg-${delay})`}
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={inView ? { pathLength: 1, opacity: 1 } : {}}
        transition={{ delay, duration: 0.6, ease: "easeOut" }}
      />
      {/* Traveling dot */}
      <motion.circle
        r="2.5"
        fill="hsl(var(--primary))"
        fillOpacity="0.7"
        initial={{ cx: 0, cy: 12, opacity: 0 }}
        animate={
          inView
            ? {
                cx: [0, 42],
                opacity: [0, 1, 1, 0],
              }
            : {}
        }
        transition={{
          delay: delay + 0.3,
          duration: 1.2,
          repeat: Infinity,
          repeatDelay: 2,
          ease: "easeInOut",
        }}
      />
      {/* Chevron arrow */}
      <motion.path
        d="M38,8 L44,12 L38,16"
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.4"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: delay + 0.5, duration: 0.3 }}
      />
    </svg>
  </div>
);

/* Solid gradient flow connector (vertical) with animated traveling dot */
const VerticalFlowLine = ({
  delay = 0,
  inView,
  height = 24,
}: {
  delay?: number;
  inView: boolean;
  height?: number;
}) => (
  <div className="flex justify-center">
    <svg
      width="24"
      height={height}
      viewBox={`0 0 24 ${height}`}
      className="overflow-visible"
    >
      <defs>
        <linearGradient id={`vg-${delay}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop
            offset="0%"
            stopColor="hsl(var(--primary))"
            stopOpacity="0.12"
          />
          <stop
            offset="50%"
            stopColor="hsl(var(--primary))"
            stopOpacity="0.4"
          />
          <stop
            offset="100%"
            stopColor="hsl(var(--primary))"
            stopOpacity="0.12"
          />
        </linearGradient>
      </defs>
      {/* Base line */}
      <motion.line
        x1="12"
        y1="0"
        x2="12"
        y2={height}
        stroke={`url(#vg-${delay})`}
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={inView ? { pathLength: 1, opacity: 1 } : {}}
        transition={{ delay, duration: 0.5, ease: "easeOut" }}
      />
      {/* Traveling dot */}
      <motion.circle
        r="2.5"
        fill="hsl(var(--primary))"
        fillOpacity="0.7"
        cx={12}
        initial={{ cy: 0, opacity: 0 }}
        animate={
          inView
            ? {
                cy: [0, height],
                opacity: [0, 1, 1, 0],
              }
            : {}
        }
        transition={{
          delay: delay + 0.2,
          duration: 1,
          repeat: Infinity,
          repeatDelay: 2.5,
          ease: "easeInOut",
        }}
      />
      {/* Chevron arrow */}
      <motion.path
        d={`M7,${height - 5} L12,${height + 1} L17,${height - 5}`}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.4"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: delay + 0.4, duration: 0.3 }}
      />
    </svg>
  </div>
);

const WhatWeDoSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="what-we-do"
      className="py-14 md:py-20 bg-background relative overflow-hidden"
    >
      <div className="container px-4" ref={ref}>
        {/* Header */}
        <div className="text-center mb-10">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4 }}
            className="text-sm font-semibold text-primary mb-2 tracking-wide uppercase"
          >
            What We Do
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-2xl md:text-4xl font-display font-bold text-foreground leading-tight tracking-tight"
          >
            We weave the fabric of{" "}
            <span className="text-gradient">global trade.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 text-lg md:text-xl text-muted-foreground max-w-4xl mx-auto leading-snug"
          >
            Using AI model fine tuned with historic trade data we do intelligent
            procurement and end-to-end orchestration with Logistics, QA and
            compliance resulting in smooth international trade.
          </motion.p>
        </div>

        {/* Desktop */}
        <div className="hidden md:block max-w-[900px] mx-auto">
          {/* Row 1: Data → AI → Procurement */}
          <div className="grid grid-cols-[1fr_48px_1.2fr_48px_1fr] items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-2xl border border-border bg-card p-5 text-center shadow-sm"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Database className="w-6 h-6 text-primary" />
              </div>
              <p className="text-base font-display font-extrabold text-foreground">
                Historic Trade Data
              </p>
              <p className="text-xs font-semibold text-muted-foreground mt-0.5">
                Years of real intelligence
              </p>
            </motion.div>

            <FlowLine delay={0.6} inView={inView} />

            {/* AI Engine */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm"
            >
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-3 shadow-lg shadow-primary/30">
                <Brain className="w-8 h-8 text-primary-foreground" />
              </div>
              <p className="text-lg font-display font-extrabold text-foreground">
                AI Engine
              </p>
              <p className="text-xs font-semibold text-muted-foreground mt-0.5">
                Pattern recognition & prediction
              </p>
            </motion.div>

            <FlowLine delay={0.8} inView={inView} />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 text-center shadow-sm backdrop-blur-sm"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <ShoppingCart className="w-6 h-6 text-primary" />
              </div>
              <p className="text-base font-display font-extrabold text-foreground">
                Intelligent Procurement
              </p>
              <p className="text-xs font-semibold text-primary">
                Data-driven sourcing
              </p>
            </motion.div>
          </div>

          {/* Vertical bridge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.7 }}
            className="flex flex-col items-center py-1"
          >
            <VerticalFlowLine delay={1} inView={inView} />
            <span className="text-xs font-extrabold text-primary/70 uppercase tracking-[0.15em] py-1">
              procurement data enables
            </span>
            <VerticalFlowLine delay={1.2} inView={inView} />
          </motion.div>

          {/* Embedded Finance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="max-w-xs mx-auto"
          >
            <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 text-center shadow-sm backdrop-blur-sm">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Banknote className="w-6 h-6 text-primary" />
              </div>
              <p className="text-base font-display font-extrabold text-foreground">
                Payment Terms
              </p>
              <p className="text-xs font-semibold text-primary">
                Extended payment terms built into the flow
              </p>
            </div>
          </motion.div>

          {/* Vertical bridge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 1 }}
            className="flex flex-col items-center py-1"
          >
            <VerticalFlowLine delay={1.4} inView={inView} />
            <span className="text-xs font-extrabold text-primary/70 uppercase tracking-[0.15em] py-1">
              powered by
            </span>
            <VerticalFlowLine delay={1.6} inView={inView} />
          </motion.div>

          {/* Orchestration → Smooth Trade */}
          <div className="grid grid-cols-[1.4fr_48px_1fr] items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 1.1 }}
              className="rounded-2xl border border-border bg-card p-5 shadow-sm"
            >
              <p className="text-xs font-extrabold text-muted-foreground uppercase tracking-widest mb-4 text-center">
                End-to-End Orchestration
              </p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Ship, label: "Logistics", sub: "Freight & delivery" },
                  {
                    icon: ShieldCheck,
                    label: "Quality",
                    sub: "Inspection & QA",
                  },
                  { icon: FileCheck, label: "Compliance", sub: "Regulatory" },
                ].map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.2 + i * 0.1, duration: 0.4 }}
                    className="flex flex-col items-center text-center bg-background rounded-xl py-3.5 px-2 border border-border"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-1.5">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <p className="text-sm font-display font-extrabold text-foreground">
                      {item.label}
                    </p>
                    <p className="text-[10px] font-semibold text-muted-foreground">
                      {item.sub}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <FlowLine delay={1.8} inView={inView} />

            {/* Smooth Trade result */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 1.5 }}
              className="rounded-2xl border border-border bg-card p-6 shadow-sm"
            >
              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 mb-2 bg-primary px-3 py-1 rounded-full">
                  <Zap className="w-3.5 h-3.5 text-primary-foreground" />
                  <span className="text-xs font-extrabold text-primary-foreground uppercase tracking-wider">
                    Result
                  </span>
                </div>
                <h3 className="text-2xl font-display font-extrabold text-foreground">
                  Smooth Trade
                </h3>
                <p className="text-base font-semibold text-muted-foreground mt-1 leading-snug">
                  End-to-end procurement, extended payment terms,
                  quality-assured, and delivered — all from one platform.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden max-w-sm mx-auto">
          <div className="space-y-0">
            {[
              { icon: Database, label: "Historic Trade Data", accent: false },
              { icon: Brain, label: "AI Engine", accent: true },
              { icon: ShoppingCart, label: "Procurement", accent: false },
              { icon: Banknote, label: "Embedded Finance", accent: false },
            ].map((item, i) => (
              <div key={item.label}>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.1 + i * 0.1, duration: 0.4 }}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                    item.accent
                      ? "bg-primary/5 border-primary/20"
                      : "bg-card border-border"
                  }`}
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-sm font-display font-bold text-foreground">
                    {item.label}
                  </p>
                </motion.div>
                {i < 3 && (
                  <div className="flex justify-center py-0.5">
                    <VerticalFlowLine
                      delay={0.3 + i * 0.12}
                      inView={inView}
                      height={16}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-center py-0.5">
            <VerticalFlowLine delay={0.65} inView={inView} height={16} />
          </div>

          <div className="grid grid-cols-3 gap-2 mb-1">
            {[
              { icon: Ship, label: "Logistics" },
              { icon: ShieldCheck, label: "QA" },
              { icon: FileCheck, label: "Compliance" },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 10 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.55 + i * 0.08, duration: 0.4 }}
                className="flex flex-col items-center bg-card border border-border rounded-xl py-3"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-1">
                  <item.icon className="w-4 h-4 text-primary" />
                </div>
                <span className="text-[11px] font-display font-bold text-foreground">
                  {item.label}
                </span>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center py-0.5">
            <VerticalFlowLine delay={0.85} inView={inView} height={16} />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8, duration: 0.4 }}
            className="rounded-xl border-2 border-primary/25 bg-card p-4 text-center"
          >
            <div className="inline-flex items-center gap-1.5 mb-1 bg-primary/10 px-3 py-1 rounded-full">
              <Zap className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] font-bold text-primary uppercase tracking-wider">
                Result
              </span>
            </div>
            <h3 className="text-lg font-display font-bold text-foreground">
              Smooth Trade
            </h3>
            <p className="text-xs text-muted-foreground">
              End-to-end procurement, financed, quality-assured & delivered.
            </p>
          </motion.div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to={"/signup"}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
